import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ChatHeaderProps } from '../interfaces';
import { CustomerStateSelector } from '../../../customers/components/CustomerStateSelector';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { CustomerStateBottomSheet } from '../../../customers/components/CustomerStateBottomSheet';
import { useCustomerStates } from '../../../customer-states/hooks/useCustomerStates';
import { useChatSearch } from './hooks/useChatSearch';
import { CustomerStateDisplay } from './CustomerStateDisplay';
import { EditCustomerOffcanvas } from './modals/EditCustomerOffcanvas';

const formatSeparatorDate = (dateString?: string | null) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const diffDays = (today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24);

    const time = date.toLocaleTimeString('es-EC', {
        hour: '2-digit',
        minute: '2-digit'
    });

    if (diffDays === 0) return `Hoy ${time}`;
    if (diffDays === 1) return `Ayer ${time}`;

    return date.toLocaleDateString('es-EC', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

const ChatHeader: React.FC<ChatHeaderProps> = ({
    selectedContact,
    selectedContactId,
    isAdmin,
    activeAgentName,
    setIsModalOpen,
    onJumpToMessage,
    onBack,
    onUpdateCustomerState,
    onUpdateCustomerInfo,
    onOpenStateHistory,
    isChatClosed = false
}) => {
    const [showHeaderSearch, setShowHeaderSearch] = useState(false);
    const [isHeaderFocused, setIsHeaderFocused] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showStateBottomSheet, setShowStateBottomSheet] = useState(false);
    const [showEditCustomer, setShowEditCustomer] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);

    const { allStates, isFetching } = useCustomerStates();

    const {
        searchTerm,
        setSearchTerm,
        searchMatches,
        currentMatchIndex,
        totalSearchMatches,
        isSearching,
        handleNextMatch,
        handlePrevMatch,
        debouncedSearchMessages,
    } = useChatSearch({ selectedContactId, onJumpToMessage });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setShowMobileMenu(false);
            }
        };

        if (showMobileMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMobileMenu]);


    const handleSearchToggle = useCallback(() => {
        if (showHeaderSearch) {
            setShowHeaderSearch(false);
            setSearchTerm('');
        } else {
            setShowHeaderSearch(true);
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 0);
        }
    }, [showHeaderSearch, setSearchTerm]);

    return (
        <div
            className="p-3 px-4 border-bottom flex-shrink-0 bg-white d-flex align-items-center justify-content-between gap-3"
            style={{ height: '85px' }}>
            {/* LEFT SIDE: Back Button & Avatar & Info */}
            <div className="d-flex align-items-center flex-grow-1 min-w-0" style={{ minWidth: 0 }}>
                {/* Back Button - Mobile Only */}
                <button
                    onClick={onBack}
                    className="btn btn-link text-muted d-lg-none me-2 p-0"
                    style={{ minWidth: 'auto' }}
                    title="Volver a contactos"
                >
                    <i className="ri-arrow-left-line fs-20"></i>
                </button>

                {/* Avatar & Info - Hidden on mobile when searching */}
                <div className={`d-flex align-items-center flex-grow-1 min-w-0 ${showHeaderSearch ? 'd-none d-lg-flex' : ''}`} style={{ minWidth: 0 }}>
                    <div className="flex-shrink-0 me-3">
                        <div
                            className="avatar-sm d-flex align-items-center justify-content-center bg-light rounded-circle text-primary">
                            <i className="ri-user-3-line fs-20"></i>
                        </div>
                    </div>
                    <div className="flex-grow-1 min-w-0" style={{ minWidth: 0 }}>
                        <h5 className="fs-14 mb-0 text-truncate overflow-hidden"
                            style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            {`${selectedContact?.customer?.firstName ?? ''} ${selectedContact?.customer?.lastName ?? ''}` || selectedContact?.name}
                        </h5>

                        <div className="mb-0 text-truncate overflow-hidden d-flex align-items-center"
                            style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            <span className="me-1">{selectedContact?.waId}</span>
                            {'-'}
                            <CustomerStateDisplay
                                allStates={allStates}
                                isFetching={isFetching}
                                selectedContact={selectedContact}
                                onStateClick={() => setShowStateBottomSheet(true)}
                            />

                            {/* Bot Activo */}
                            {selectedContact?.botActive && (
                                <span className="badge bg-success align-middle d-none d-lg-inline-block ms-1"
                                    style={{ fontSize: '10px' }}>
                                    Bot Activo
                                </span>
                            )}

                            {/* Agente Activo - Desktop */}
                            {isAdmin && activeAgentName && (
                                <span className="badge bg-primary align-middle d-none d-lg-inline-block ms-1"
                                    style={{ fontSize: '10px' }}>
                                    Agente: {activeAgentName}
                                </span>
                            )}

                            {isAdmin && !activeAgentName && !selectedContact?.botActive && (
                                <span className="badge bg-warning align-middle d-none d-lg-inline-block ms-1"
                                    style={{ fontSize: '10px' }}>
                                    Sin agente
                                </span>
                            )}


                        </div>

                        <p
                            className="text-muted fs-12 mb-0 text-truncate overflow-hidden d-flex align-items-center gap-1"
                            style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                        >
                            {selectedContact?.lastMessageAt && (
                                <span className="text-muted fs-11 text-truncate">
                                    Último mensaje: {formatSeparatorDate(selectedContact.lastMessageAt)}
                                </span>
                            )}

                            {/* Agente Activo - Mobile */}
                            {isAdmin && activeAgentName && (
                                <span
                                    className="badge bg-primary align-middle d-lg-none flex-shrink-0"
                                    style={{ fontSize: '10px' }}
                                >
                                    Agente: {activeAgentName}
                                </span>
                            )}

                            {/* BOT EN MOBILE */}
                            {selectedContact?.botActive && (
                                <span
                                    className="badge bg-success align-middle d-lg-none flex-shrink-0"
                                    style={{ fontSize: '10px' }}
                                >
                                    Bot Activo
                                </span>
                            )}
                            {isAdmin && !activeAgentName && !selectedContact?.botActive && (
                                <span className="badge bg-warning align-middle d-lg-none flex-shrink-0 ms-1"
                                    style={{ fontSize: '10px' }}>
                                    Sin agente
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            {/* RIGHT SIDE: Actions */}
            <div
                className="d-flex align-items-center justify-content-end gap-2"
                style={{
                    transition: 'all 0.3s ease',
                    flex: showHeaderSearch ? '1 1 auto' : '0 0 auto',
                    maxWidth: showHeaderSearch ? '100%' : 'auto'
                }}
            >
                {/* Mobile Menu - 3 dots */}
                <div className={`d-lg-none ${showHeaderSearch ? 'd-none' : ''} position-relative`} ref={mobileMenuRef}>
                    <div className="dropdown">
                        <button
                            className="btn btn-icon btn-ghost-secondary p-1"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowMobileMenu(!showMobileMenu);
                            }}
                        >
                            <i className="ri-more-2-fill fs-22 text-muted"></i>
                        </button>
                        <ul
                            className={`dropdown-menu dropdown-menu-end shadow-lg ${showMobileMenu ? 'show' : ''}`}
                            style={{
                                minWidth: '150px',
                                position: 'absolute',
                                right: '0',
                                left: 'auto',
                                top: '100%',
                                margin: '8px 0 0',
                                zIndex: 1060
                            }}
                        >
                            <li>
                                <button
                                    className="dropdown-item d-flex align-items-center gap-2"
                                    onClick={() => {
                                        setShowMobileMenu(false);
                                        handleSearchToggle();
                                    }}
                                    title="Buscar mensajes"
                                >
                                    <i className="ri-search-line"></i>
                                    Buscar
                                </button>
                            </li>
                            <li>
                                <button
                                    className="dropdown-item d-flex align-items-center gap-2"
                                    onClick={() => {
                                        setShowMobileMenu(false);
                                        setIsModalOpen(true);
                                    }}
                                    title="Asignar"
                                    disabled={isChatClosed}
                                >
                                    <i className="ri-corner-up-right-line"></i>
                                    Asignar
                                </button>
                            </li>
                            <li>
                                <button
                                    className="dropdown-item d-flex align-items-center gap-2"
                                    onClick={() => {
                                        setShowMobileMenu(false);
                                        onOpenStateHistory?.();
                                    }}
                                    title="Ver Historial de Estados"
                                >
                                    <i className="ri-history-line"></i>
                                    Historial de Estados
                                </button>
                            </li>
                            {selectedContact?.customer?.id && (
                                <li>
                                    <button
                                        className="dropdown-item d-flex align-items-center gap-2"
                                        onClick={() => {
                                            setShowMobileMenu(false);
                                            setShowEditCustomer(true);
                                        }}
                                        title="Editar cliente"
                                    >
                                        <i className="ri-user-settings-line"></i>
                                        Editar cliente
                                    </button>
                                </li>
                            )}
                            {selectedContact?.customer?.id && selectedContact?.customer?.currentStateId && (
                                <li>
                                    <button
                                        className="dropdown-item d-flex align-items-center gap-2"
                                        onClick={() => {
                                            setShowMobileMenu(false);
                                            setShowStateBottomSheet(true);
                                        }}
                                        title="Cambiar estado"
                                    >
                                        <i className="ri-git-branch-line"></i>
                                        Cambiar estado
                                    </button>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>

                {/* Search Bar - Visible on both mobile and desktop when searching */}
                {showHeaderSearch && (
                    <div
                        className={`flex-grow-1 d-flex align-items-center bg-light rounded-pill px-3 h-[40px] border w-100 ${isHeaderFocused ? 'border-success' : 'border-light'}`}
                        style={{ transition: 'width 0.3s ease' }}>
                        <svg viewBox="0 0 24 24" height="18" width="18" fill="#667781"
                            className="flex-shrink-0 mt-1">
                            <path
                                d="M15.009 13.805h-.636l-.227-.217C15.59 12.222 16.53 10.45 16.53 8.515 16.53 4.364 13.166 1 9.015 1 4.863 1 1.5 4.364 1.5 8.515c0 4.151 3.363 7.515 7.515 7.515 1.935 0 3.707-.94 5.073-2.384l.217.227v.636l4.009 4.009 1.191-1.191-4.009-4.009zm-6.009 0c-2.932 0-5.315-2.383-5.315-5.315 0-2.932 2.383-5.315 5.315-5.315 2.932 0 5.315 2.383 5.315 5.315 0 2.932-2.383 5.315-5.315 5.315z" />
                        </svg>

                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Buscar mensajes..."
                            className="form-control form-control-sm border-0 bg-transparent shadow-none ms-2 mt-1 mb-1"
                            style={{
                                fontSize: '14px',
                                color: '#3b4a54',
                                outline: 'none',
                                minWidth: 0
                            }}
                            value={searchTerm}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearchTerm(value);
                                debouncedSearchMessages(value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && searchMatches.length > 0) {
                                    e.preventDefault();
                                    handleNextMatch();
                                }
                                if (e.shiftKey && e.key === 'Enter' && searchMatches.length > 0) {
                                    e.preventDefault();
                                    handlePrevMatch();
                                }
                            }}
                            onFocus={() => setIsHeaderFocused(true)}
                            onBlur={() => setIsHeaderFocused(false)}
                        />

                        {isSearching && (
                            <div
                                className="spinner-border spinner-border-sm text-muted ms-2 flex-shrink-0"
                                role="status"></div>
                        )}
                        {!isSearching && searchMatches.length > 0 && (
                            <div className="d-flex align-items-center ms-2 flex-shrink-0">
                                <span
                                    className="text-muted fs-12 me-2">{currentMatchIndex + 1} de {totalSearchMatches}</span>
                                <div className="d-flex border-start ps-2">
                                    <button
                                        className="btn btn-sm btn-link p-0 text-muted me-2"
                                        onClick={handleNextMatch}
                                        disabled={currentMatchIndex === searchMatches.length - 1}
                                        title="Buscar más arriba"
                                    >
                                        <i className="ri-arrow-up-line fs-18"></i>
                                    </button>
                                    <button
                                        className="btn btn-sm btn-link p-0 text-muted"
                                        onClick={handlePrevMatch}
                                        disabled={currentMatchIndex === 0}
                                        title="Buscar más abajo"
                                    >
                                        <i className="ri-arrow-down-line fs-18"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                        {!isSearching && searchTerm && searchMatches.length === 0 && (
                            <span
                                className="text-muted fs-12 ms-2 flex-shrink-0">0 resultados</span>
                        )}
                    </div>
                )}

                {/* Search Toggle - Visible only on mobile when searching */}
                {showHeaderSearch && (
                    <div
                        onClick={handleSearchToggle}
                        style={{ cursor: 'pointer' }}
                        className="flex-shrink-0 d-lg-none"
                    >
                        <i className="ri-close-line fs-24 mt-1 d-inline-block"></i>
                    </div>
                )}

                {/* Desktop Actions */}
                <div className="d-none d-lg-flex align-items-center gap-2">
                    {/* Estado del Cliente */}
                    {!showHeaderSearch && selectedContact?.customer?.id && selectedContact?.customer?.currentStateId && (
                        <div className="flex-shrink-0">
                            <CustomerStateSelector
                                customerId={selectedContact.customer.id}
                                currentStateId={selectedContact.customer.currentStateId}
                                allStates={allStates}
                                onSuccess={(newState) => onUpdateCustomerState?.(selectedContactId!, newState)}
                            />
                        </div>
                    )}

                    {/* Search Toggle */}
                    <div
                        onClick={handleSearchToggle}
                        style={{ cursor: 'pointer' }}
                        className="flex-shrink-0"
                    >
                        {showHeaderSearch ? (
                            <i className="ri-close-line fs-24 mt-1 d-inline-block"></i>
                        ) : (
                            <svg viewBox="0 0 24 24" height="25" width="25" fill="#667781"
                                className="flex-shrink-0 mt-1 ms-2">
                                <path
                                    d="M15.009 13.805h-.636l-.227-.217C15.59 12.222 16.53 10.45 16.53 8.515 16.53 4.364 13.166 1 9.015 1 4.863 1 1.5 4.364 1.5 8.515c0 4.151 3.363 7.515 7.515 7.515 1.935 0 3.707-.94 5.073-2.384l.217.227v.636l4.009 4.009 1.191-1.191-4.009-4.009zm-6.009 0c-2.932 0-5.315-2.383-5.315-5.315 0-2.932 2.383-5.315 5.315-5.315 2.932 0 5.315 2.383 5.315 5.315 0 2.932-2.383 5.315-5.315 5.315z" />
                            </svg>
                        )}
                    </div>
                    {/* Dropdown Kebab Menu (⋮) */}
                    {!showHeaderSearch && (
                        <UncontrolledDropdown className="flex-shrink-0">
                            <DropdownToggle
                                tag="button"
                                className="btn btn-icon btn-ghost-secondary p-1"
                                type="button"
                            >
                                <i className="ri-more-2-fill fs-20 text-muted"></i>
                            </DropdownToggle>
                            <DropdownMenu className="dropdown-menu-end shadow-lg" style={{ zIndex: 1060 }}>
                                <DropdownItem
                                    className="d-flex align-items-center gap-2"
                                    onClick={() => setIsModalOpen(true)}
                                    disabled={isChatClosed}
                                >
                                    <i className="ri-corner-up-right-line text-muted"></i>
                                    Asignar / Escalar
                                </DropdownItem>
                                <DropdownItem
                                    className="d-flex align-items-center gap-2"
                                    onClick={onOpenStateHistory}
                                >
                                    <i className="ri-history-line text-muted"></i>
                                    Ver Historial de Estados
                                </DropdownItem>
                                {selectedContact?.customer?.id && (
                                    <DropdownItem
                                        className="d-flex align-items-center gap-2"
                                        onClick={() => setShowEditCustomer(true)}
                                    >
                                        <i className="ri-user-settings-line text-muted"></i>
                                        Editar cliente
                                    </DropdownItem>
                                )}
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    )}
                </div>
            </div>

            {/* Mobile CustomerStateBottomSheet */}
            {selectedContact?.customer?.id && selectedContact?.customer?.currentStateId && (
                <CustomerStateBottomSheet
                    isOpen={showStateBottomSheet}
                    onClose={() => setShowStateBottomSheet(false)}
                    customerId={selectedContact.customer.id}
                    currentStateId={selectedContact.customer.currentStateId}
                    currentState={selectedContact.customer.currentState}
                    allStates={allStates}
                    onSuccess={(newState) => onUpdateCustomerState?.(selectedContactId!, newState)}
                />
            )}

            {/* Edit Customer Offcanvas */}
            {selectedContact?.customer?.id && (
                <EditCustomerOffcanvas
                    isOpen={showEditCustomer}
                    onClose={() => setShowEditCustomer(false)}
                    customer={selectedContact.customer}
                    onSuccess={(data) => {
                        onUpdateCustomerInfo?.(selectedContactId!, data);
                    }}
                />
            )}
        </div>
    );
};

export default ChatHeader;
